import { 
  auth, 
  signInWithGooglePopup, 
  createAuthUserWithEmailAndPassword, 
  signInAuthUserWithEmailAndPassword,
  signOutUser 
} from '../config/firebase';
import { onAuthStateChanged } from 'firebase/auth';

class FirebaseAuthService {
  constructor() {
    this.currentUser = null;
    this.authStateListeners = [];
    
    // Listen to auth state changes
    onAuthStateChanged(auth, (user) => {
      this.currentUser = user;
      this.notifyAuthStateListeners(user);
    });
  }

  // Add listener for auth state changes
  addAuthStateListener(callback) {
    this.authStateListeners.push(callback);
    // Call immediately with current state
    callback(this.currentUser);
  }

  // Remove auth state listener
  removeAuthStateListener(callback) {
    this.authStateListeners = this.authStateListeners.filter(listener => listener !== callback);
  }

  // Notify all listeners of auth state change
  notifyAuthStateListeners(user) {
    this.authStateListeners.forEach(callback => callback(user));
  }

  // Get Firebase ID token for backend authentication
  async getIdToken() {
    if (!this.currentUser) {
      throw new Error('No user is currently signed in');
    }
    return await this.currentUser.getIdToken();
  }

  // Sign in with email and password
  async signInWithEmail(email, password) {
    try {
      console.log('🔐 [FIREBASE] Signing in with email:', email);
      const result = await signInAuthUserWithEmailAndPassword(email, password);
      console.log('✅ [FIREBASE] Email sign-in successful');
      return result;
    } catch (error) {
      console.error('❌ [FIREBASE] Email sign-in failed:', error);
      throw this.handleAuthError(error);
    }
  }

  // Sign up with email and password
  async signUpWithEmail(email, password) {
    try {
      console.log('🔐 [FIREBASE] Signing up with email:', email);
      const result = await createAuthUserWithEmailAndPassword(email, password);
      console.log('✅ [FIREBASE] Email sign-up successful');
      return result;
    } catch (error) {
      console.error('❌ [FIREBASE] Email sign-up failed:', error);
      throw this.handleAuthError(error);
    }
  }

  // Sign in with Google
  async signInWithGoogle() {
    try {
      console.log('🔐 [FIREBASE] Signing in with Google');
      const result = await signInWithGooglePopup();
      console.log('✅ [FIREBASE] Google sign-in successful');
      return result;
    } catch (error) {
      console.error('❌ [FIREBASE] Google sign-in failed:', error);
      throw this.handleAuthError(error);
    }
  }

  // Sign out
  async signOut() {
    try {
      console.log('🔐 [FIREBASE] Signing out');
      await signOutUser();
      console.log('✅ [FIREBASE] Sign-out successful');
    } catch (error) {
      console.error('❌ [FIREBASE] Sign-out failed:', error);
      throw this.handleAuthError(error);
    }
  }

  // Handle Firebase auth errors
  handleAuthError(error) {
    let message = 'Authentication failed. Please try again.';
    
    switch (error.code) {
      case 'auth/user-disabled':
        message = 'This account has been disabled.';
        break;
      case 'auth/user-not-found':
        message = 'No account found with this email address.';
        break;
      case 'auth/wrong-password':
        message = 'Incorrect password.';
        break;
      case 'auth/email-already-in-use':
        message = 'An account with this email already exists.';
        break;
      case 'auth/weak-password':
        message = 'Password should be at least 6 characters.';
        break;
      case 'auth/invalid-email':
        message = 'Invalid email address.';
        break;
      case 'auth/too-many-requests':
        message = 'Too many failed attempts. Please try again later.';
        break;
      case 'auth/popup-closed-by-user':
        message = 'Sign-in popup was closed before completing.';
        break;
      case 'auth/cancelled-popup-request':
        message = 'Only one popup request is allowed at one time.';
        break;
      default:
        console.error('Unknown auth error:', error);
        break;
    }
    
    return new Error(message);
  }

  // Get current user info
  getCurrentUser() {
    return this.currentUser;
  }

  // Check if user is signed in
  isSignedIn() {
    return !!this.currentUser;
  }
}

// Export singleton instance
export const firebaseAuthService = new FirebaseAuthService();
export default firebaseAuthService;
